import { useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import StoredFile from 'components/StoredFile';

import { useErrorHandler } from 'lib';
import { OrderEditFlightTicket_order$key } from '__generated__/OrderEditFlightTicket_order.graphql';
import { OrderEditFlightTicketUploadMutation } from '__generated__/OrderEditFlightTicketUploadMutation.graphql';
import { OrderEditFlightTicketDeleteMutation } from '__generated__/OrderEditFlightTicketDeleteMutation.graphql';

type Props = {
  order: OrderEditFlightTicket_order$key;
};

const accept = ['application/pdf'];

const OrderEditFlightTicket = ({ order }: Props) => {
  const errorHandler = useErrorHandler();

  const { id, flightTicket } = useFragment(
    graphql`
      fragment OrderEditFlightTicket_order on Order {
        id

        flightTicket {
          ...StoredFile_file
        }
      }
    `,
    order
  );

  const [upload, isUploading] =
    useMutation<OrderEditFlightTicketUploadMutation>(graphql`
      mutation OrderEditFlightTicketUploadMutation($id: ID!, $file: Upload!) {
        uploadOrderFlightTicket(id: $id, file: $file) {
          flightTicket {
            ...StoredFile_file
          }
        }
      }
    `);

  const [deleteInvoice, isDeleting] =
    useMutation<OrderEditFlightTicketDeleteMutation>(
      graphql`
        mutation OrderEditFlightTicketDeleteMutation($id: ID!) {
          deleteOrderFlightTicket(id: $id) {
            flightTicket {
              ...StoredFile_file
            }
          }
        }
      `
    );

  const handleDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length < 1) return;

      upload({
        variables: { id, file: null },
        uploadables: { file: accepted[0] },
        ...errorHandler(),
      });
    },
    [errorHandler, id, upload]
  );

  const handleDelete = useCallback(() => {
    deleteInvoice({
      variables: { id },
      ...errorHandler(),
    });
  }, [deleteInvoice, errorHandler, id]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography>Skrydžio bilietas klientui</Typography>

      <StoredFile
        file={flightTicket}
        loading={isUploading || isDeleting}
        accept={accept}
        disabled={Boolean(flightTicket)}
        rejectMessage="Netinkamas failo formatas? (Galima įkelti tik pdf documentus)"
        onDrop={handleDrop}
        onDelete={handleDelete}
      />
    </Box>
  );
};

export default OrderEditFlightTicket;
